import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { userService } from '../services/api';
import subscriptionService, { SubscriptionInfo } from '../services/subscriptionApi';
import { useAstralInfo } from '../hooks/useAstralInfo';
import { useSpiritualStats } from '../hooks/useSpiritualStats';
import ContactsTab from '../components/ContactsTab';
import CrescimentoPessoal from '../components/spiritual/CrescimentoPessoal';
import NumerologiaPessoal from '../components/spiritual/NumerologiaPessoal';
import CorDoDia from '../components/spiritual/CorDoDia';
import ProgressoAccordion from '../components/spiritual/ProgressoAccordion';
import { BR_STATES, isBrazil } from '../data/brazilStates';
import { useBrazilCities } from '../hooks/useBrazilCities';
import BirthTimeSelect, { isCompleteTime, isPartialTime } from '../components/BirthTimeSelect';

type Tab = 'jornada' | 'dados' | 'conexoes' | 'pagamentos' | 'seguranca';

interface ProfileForm {
  name: string;
  email: string;
  birth_date: string;
  birth_time: string;
  gender: string;
  birth_city: string;
  birth_state: string;
  birth_country: string;
  marital_status: string;
  profession: string;
}

interface PasswordForm {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const GENDER_OPTIONS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'feminino', label: 'Feminino' },
  { value: 'não-binário', label: 'Não-binário' },
  { value: 'prefiro não dizer', label: 'Prefiro não dizer' },
];

const MARITAL_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'solteiro', label: 'Solteiro(a)' },
  { value: 'namorando', label: 'Namorando' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viúvo', label: 'Viúvo(a)' },
  { value: 'união estável', label: 'União estável' },
];

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 transition-colors';
const selectClass =
  'w-full px-4 py-3 rounded-xl border border-white/10 text-white focus:outline-none focus:border-purple-400 transition-colors';
const labelClass = 'block text-sm text-white/60 mb-1';

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'jornada'
  );

  const { astralInfo } = useAstralInfo();
  const { stats } = useSpiritualStats();

  useEffect(() => {
    const tab = searchParams.get('tab') as Tab;
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const [profileForm, setProfileForm] = useState<ProfileForm>({
    name: '',
    email: '',
    birth_date: '',
    birth_time: '',
    gender: '',
    birth_city: '',
    birth_state: '',
    birth_country: 'Brasil',
    marital_status: '',
    profession: '',
  });

  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [birthChartInvalidated, setBirthChartInvalidated] = useState(false);
  const [origBirth, setOrigBirth] = useState({ date: '', time: '', city: '', state: '', country: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [loading, setLoading] = useState(true);

  const [paymentHistory, setPaymentHistory] = useState<{
    payments: any[];
    token_transactions: any[];
  } | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadPayments = async () => {
    if (paymentHistory) return;
    setLoadingPayments(true);
    try {
      const data = await userService.getPaymentHistory();
      setPaymentHistory(data);
    } catch {
      setPaymentHistory({ payments: [], token_transactions: [] });
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadSubscription = async () => {
    setLoadingSubscription(true);
    try {
      const data = await subscriptionService.getMySubscription();
      setSubscription(data);
    } catch {
      setSubscription({ has_subscription: false });
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelConfirm) {
      setCancelConfirm(true);
      return;
    }
    setCancelling(true);
    try {
      await subscriptionService.cancelSubscription();
      await loadSubscription();
    } catch {
      // silencioso: card volta a mostrar o status atual via loadSubscription mesmo se falhar
    } finally {
      setCancelling(false);
      setCancelConfirm(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pagamentos') {
      loadPayments();
      loadSubscription();
    }
  }, [activeTab]);

  useEffect(() => {
    userService.getMe().then((data: any) => {
      const p = data.profile;
      setProfileForm({
        name: data.name || '',
        email: data.email || '',
        birth_date: p?.birth_date ? p.birth_date.slice(0, 10) : '',
        birth_time: p?.birth_time ? p.birth_time.slice(0, 5) : '',
        gender: p?.gender || '',
        birth_city: p?.birth_city || '',
        birth_state: p?.birth_state || '',
        birth_country: p?.birth_country || 'Brasil',
        marital_status: p?.marital_status || '',
        profession: p?.profession || '',
      });
      setOrigBirth({
        date: p?.birth_date ? p.birth_date.slice(0, 10) : '',
        time: p?.birth_time ? p.birth_time.slice(0, 5) : '',
        city: p?.birth_city || '',
        state: p?.birth_state || '',
        country: p?.birth_country || 'Brasil',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setProfileForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Select de cidade por UF (evita erro de digitação que atrapalha a geocodificação
  // do mapa astral) — só disponível pra Brasil; fora disso cai pra texto livre.
  const brazilProfile = isBrazil(profileForm.birth_country);
  const { cities: brCitiesProfile, loading: citiesLoadingProfile, failed: citiesFailedProfile } = useBrazilCities(
    brazilProfile ? profileForm.birth_state : null
  );
  const showCitySelectProfile = brazilProfile && !!profileForm.birth_state && !citiesFailedProfile;
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProfileForm(prev => ({ ...prev, birth_state: e.target.value, birth_city: '' }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (profileForm.name.trim().split(/\s+/).filter(Boolean).length < 2) {
      setProfileMsg({ type: 'error', text: 'Digite seu nome completo (nome e sobrenome) — a numerologia usa todas as letras do seu nome.' });
      return;
    }
    if (isPartialTime(profileForm.birth_time)) {
      setProfileMsg({ type: 'error', text: 'Selecione a hora e o minuto do nascimento, ou deixe os dois em branco.' });
      return;
    }

    setSavingProfile(true);
    setProfileMsg(null);
    setBirthChartInvalidated(false);
    const birthWillChange =
      (profileForm.birth_date && profileForm.birth_date !== origBirth.date) ||
      (profileForm.birth_time && profileForm.birth_time !== origBirth.time) ||
      (profileForm.birth_city && profileForm.birth_city !== origBirth.city) ||
      (profileForm.birth_state && profileForm.birth_state !== origBirth.state) ||
      (profileForm.birth_country && profileForm.birth_country !== origBirth.country);
    try {
      await userService.updateUser({ name: profileForm.name, email: profileForm.email });
      const profilePayload: Record<string, any> = {};
      if (profileForm.birth_date) profilePayload.birth_date = profileForm.birth_date;
      if (isCompleteTime(profileForm.birth_time)) profilePayload.birth_time = profileForm.birth_time;
      if (profileForm.gender) profilePayload.gender = profileForm.gender;
      if (profileForm.birth_city) profilePayload.birth_city = profileForm.birth_city;
      if (profileForm.birth_state) profilePayload.birth_state = profileForm.birth_state;
      if (profileForm.birth_country) profilePayload.birth_country = profileForm.birth_country;
      if (profileForm.marital_status) profilePayload.marital_status = profileForm.marital_status;
      if (profileForm.profession) profilePayload.profession = profileForm.profession;
      if (Object.keys(profilePayload).length > 0) {
        await userService.updateProfile(profilePayload);
      }
      // Recarregar usuário completo e atualizar formulário
      const updated = await userService.getMe();
      const p2 = (updated as any).profile;
      // Invalidar cache do dashboard
      if (user) {
        const oldP = (user as any).profile;
        localStorage.removeItem(`astralInfo:${user.id}:${oldP?.birth_date || ''}`);
      }
      const newDate = p2?.birth_date ? p2.birth_date.slice(0, 10) : '';
      const newTime = p2?.birth_time ? p2.birth_time.slice(0, 5) : '';
      const newCity = p2?.birth_city || '';
      const newState = p2?.birth_state || '';
      const newCountry = p2?.birth_country || 'Brasil';
      setProfileForm({
        name: updated.name || '',
        email: updated.email || '',
        birth_date: newDate,
        birth_time: newTime,
        gender: p2?.gender || '',
        birth_city: newCity,
        birth_state: newState,
        birth_country: newCountry,
        marital_status: p2?.marital_status || '',
        profession: p2?.profession || '',
      });
      setOrigBirth({ date: newDate, time: newTime, city: newCity, state: newState, country: newCountry });
      if (birthWillChange) {
        setBirthChartInvalidated(true);
        setProfileMsg({ type: 'success', text: 'Dados atualizados! Seu mapa astral foi resetado.' });
      } else {
        setProfileMsg({ type: 'success', text: 'Perfil atualizado com sucesso!' });
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e: any) => e.msg).join(' | ')
        : (detail || err?.message || 'Erro ao salvar perfil');
      setProfileMsg({ type: 'error', text: msg });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await userService.deleteAccount();
      logout();
      navigate('/');
    } catch {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMsg({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }
    if (passwordForm.new_password.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Nova senha deve ter pelo menos 6 caracteres' });
      return;
    }
    setSavingPassword(true);
    try {
      await userService.changePassword(passwordForm.current_password, passwordForm.new_password);
      setPasswordMsg({ type: 'success', text: 'Senha alterada com sucesso!' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setPasswordMsg({ type: 'error', text: detail || 'Erro ao alterar senha' });
    } finally {
      setSavingPassword(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '1rem',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <div className="text-white/50 text-sm">Carregando perfil...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-mystical theme-transition theme-bg-background">

      <div className={`${activeTab === 'jornada' ? 'max-w-5xl' : 'max-w-2xl'} mx-auto px-4 py-5 sm:py-10`}>
        <div className="mb-4 sm:mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-white/50 hover:text-white transition-colors text-sm flex-shrink-0"
          >
            ← Dashboard
          </button>
          <h1 className="text-xl sm:text-3xl font-bold theme-text-primary flex-1">Meu Perfil</h1>
          <button
            onClick={() => setActiveTab('dados')}
            title="Dados pessoais"
            className="p-2 rounded-lg transition-all flex-shrink-0"
            style={activeTab === 'dados'
              ? { background: 'linear-gradient(to right, #7c3aed, #8b5cf6)', color: '#fff' }
              : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }
            }
          >
            <Cog6ToothIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Abas — sem ícone, só texto (pedido do usuário): "💳 Pagamentos" quebrava
            ao meio (ícone em cima, palavra embaixo) na largura apertada de 4 abas
            iguais no celular, enquanto "Jornada"/"Conexões" cabiam numa linha só —
            tirar o ícone resolve a inconsistência de vez, sem depender de largura. */}
        <div className="flex gap-1 mb-4 sm:mb-6 p-1 rounded-xl flex-wrap" style={{ background: 'rgba(255,255,255,0.05)' }}>
          {([
            { key: 'jornada', label: 'Jornada' },
            { key: 'conexoes', label: 'Conexões' },
            { key: 'pagamentos', label: 'Pagamentos' },
            { key: 'seguranca', label: 'Segurança' },
          ] as { key: Tab; label: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 py-2 px-1 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap"
              style={activeTab === tab.key
                ? { background: 'linear-gradient(to right, #7c3aed, #8b5cf6)', color: '#fff' }
                : { color: 'rgba(255,255,255,0.5)' }
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Aba: Jornada Espiritual */}
        {activeTab === 'jornada' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="space-y-6">
              <CrescimentoPessoal astralInfo={astralInfo} />
              <ProgressoAccordion stats={stats} />
            </div>
            <div className="space-y-6">
              <NumerologiaPessoal astralInfo={astralInfo} />
              <CorDoDia astralInfo={astralInfo} />
            </div>
          </div>
        )}

        {/* Aba: Dados pessoais */}
        {activeTab === 'dados' && <div style={cardStyle} className="p-4 sm:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <button
              onClick={() => setActiveTab('jornada')}
              className="text-white/50 hover:text-white transition-colors text-sm flex-shrink-0"
            >
              ← Voltar
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-white">Dados Pessoais</h2>
          </div>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className={labelClass}>Nome completo</label>
                <input
                  type="text"
                  name="name"
                  value={profileForm.name}
                  onChange={handleProfileChange}
                  className={inputClass}
                  required
                />
                <p className="text-white/40 text-xs mt-1">Nome e sobrenome — usado no cálculo da numerologia.</p>
              </div>
              <div>
                <label className={labelClass}>E-mail</label>
                <input
                  type="email"
                  name="email"
                  value={profileForm.email}
                  onChange={handleProfileChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className={labelClass}>Data de nascimento</label>
                <input
                  type="date"
                  name="birth_date"
                  value={profileForm.birth_date}
                  onChange={handleProfileChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Horário de nascimento</label>
                <BirthTimeSelect
                  value={profileForm.birth_time}
                  onChange={(v) => setProfileForm(prev => ({ ...prev, birth_time: v }))}
                  selectClassName={selectClass}
                  selectStyle={{ background: 'rgba(255,255,255,0.05)' }}
                  optionStyle={{ background: '#1a1030' }}
                  invalid={isPartialTime(profileForm.birth_time)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className={labelClass}>Gênero</label>
                <select
                  name="gender"
                  value={profileForm.gender}
                  onChange={handleProfileChange}
                  className={selectClass}
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <option value="" style={{ background: '#1a1030' }}>Não informado</option>
                  {GENDER_OPTIONS.map(o => (
                    <option key={o.value} value={o.value} style={{ background: '#1a1030' }}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Estado civil</label>
                <select
                  name="marital_status"
                  value={profileForm.marital_status}
                  onChange={handleProfileChange}
                  className={selectClass}
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  {MARITAL_OPTIONS.map(o => (
                    <option key={o.value} value={o.value} style={{ background: '#1a1030' }}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className={labelClass}>Estado {brazilProfile ? '(UF)' : ''}</label>
                {brazilProfile ? (
                  <select
                    name="birth_state"
                    value={profileForm.birth_state}
                    onChange={handleStateChange}
                    className={selectClass}
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                  >
                    <option value="" style={{ background: '#1a1030' }}>Selecione o estado</option>
                    {BR_STATES.map(s => (
                      <option key={s.uf} value={s.uf} style={{ background: '#1a1030' }}>{s.uf} — {s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="birth_state"
                    value={profileForm.birth_state}
                    onChange={handleProfileChange}
                    className={inputClass}
                    placeholder="Estado/província"
                  />
                )}
              </div>
              <div>
                <label className={labelClass}>Cidade de nascimento</label>
                {showCitySelectProfile ? (
                  <select
                    name="birth_city"
                    value={profileForm.birth_city}
                    onChange={handleProfileChange}
                    disabled={citiesLoadingProfile}
                    className={selectClass}
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                  >
                    <option value="" style={{ background: '#1a1030' }}>
                      {citiesLoadingProfile ? 'Carregando cidades…' : 'Selecione a cidade'}
                    </option>
                    {brCitiesProfile.map(c => (
                      <option key={c} value={c} style={{ background: '#1a1030' }}>{c}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      name="birth_city"
                      value={profileForm.birth_city}
                      onChange={handleProfileChange}
                      className={inputClass}
                      placeholder={brazilProfile && !profileForm.birth_state ? 'Selecione o estado ao lado primeiro' : undefined}
                      disabled={brazilProfile && !profileForm.birth_state}
                    />
                    {citiesFailedProfile && (
                      <p className="text-amber-300 text-xs mt-1">Não foi possível carregar a lista de cidades agora — digite manualmente.</p>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
                <label className={labelClass}>Profissão</label>
                <input
                  type="text"
                  name="profession"
                  value={profileForm.profession}
                  onChange={handleProfileChange}
                  className={inputClass}
                  placeholder="Ex: Designer"
                />
            </div>

            {profileMsg && (
              <div
                className={`px-4 py-3 rounded-lg text-sm ${
                  profileMsg.type === 'success'
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                    : 'bg-red-500/20 text-red-300 border border-red-500/30'
                }`}
              >
                {profileMsg.text}
              </div>
            )}

            {birthChartInvalidated && (
              <div className="px-4 py-4 rounded-xl bg-purple-500/15 border border-purple-400/40 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm text-purple-200 font-medium">Mapa astral pronto para regenerar</p>
                  <p className="text-xs text-purple-300/70 mt-0.5">Com os dados corrigidos, seu novo mapa será calculado ao abrir o Dashboard.</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  style={{ background: 'linear-gradient(to right, #7c3aed, #8b5cf6)' }}
                >
                  Ir para o Dashboard →
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={savingProfile}
              className="w-full py-3 rounded-xl font-semibold text-white transition-all"
              style={{
                background: savingProfile
                  ? 'rgba(139,92,246,0.4)'
                  : 'linear-gradient(to right, #7c3aed, #8b5cf6)',
              }}
            >
              {savingProfile ? 'Salvando...' : 'Salvar dados pessoais'}
            </button>
          </form>
        </div>}

        {/* Aba: Conexões */}
        {activeTab === 'conexoes' && (
          <div style={cardStyle} className="p-4 sm:p-8">
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Minhas Conexões</h2>
            <ContactsTab />
          </div>
        )}

        {/* Aba: Pagamentos */}
        {activeTab === 'pagamentos' && (
          <div style={cardStyle} className="p-4 sm:p-8">
            {/* Assinatura */}
            {!loadingSubscription && subscription && (
              <div className="mb-8 px-4 py-4 rounded-xl" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)' }}>
                {subscription.has_subscription && subscription.active && !subscription.cancel_requested ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-sm text-white font-semibold">✨ Você é assinante Astralyz</p>
                      <p className="text-xs text-white/50 mt-0.5">
                        R$ {Number(subscription.plan_amount || 0).toFixed(2).replace('.', ',')}/mês ·{' '}
                        {subscription.tokens_per_cycle} tokens/ciclo · Aprofundar e Perguntar ilimitados
                        {subscription.current_period_end && (
                          <> · renova em {new Date(subscription.current_period_end).toLocaleDateString('pt-BR')}</>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className={`text-xs px-3 py-2 rounded-lg font-medium transition-colors ${
                        cancelConfirm ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30' : 'bg-white/5 text-white/50 hover:bg-white/10'
                      }`}
                    >
                      {cancelling ? 'Cancelando…' : cancelConfirm ? 'Confirmar cancelamento' : 'Cancelar assinatura'}
                    </button>
                  </div>
                ) : subscription.has_subscription && subscription.active && subscription.cancel_requested ? (
                  <p className="text-sm text-white/70">
                    Assinatura cancelada — você continua com aprofundamento e perguntas ilimitados
                    {subscription.current_period_end && (
                      <> até <strong className="text-white">{new Date(subscription.current_period_end).toLocaleDateString('pt-BR')}</strong></>
                    )}, sem nova cobrança depois disso.
                  </p>
                ) : subscription.has_subscription && subscription.status === 'pending' ? (
                  <p className="text-sm text-white/70">
                    ⏳ Assinatura aguardando confirmação do pagamento — pode levar alguns minutos após autorizar no Mercado Pago.
                  </p>
                ) : (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <p className="text-sm text-white/70">
                      Assine e ganhe <strong className="text-white">aprofundamento e perguntas ilimitados</strong> em toda leitura →
                    </p>
                    <a
                      href="/checkout?mode=subscription"
                      className="text-xs px-3 py-2 rounded-lg font-medium bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 transition-colors"
                    >
                      Assinar
                    </a>
                  </div>
                )}
              </div>
            )}

            {loadingPayments ? (
              <div className="text-white/40 text-sm text-center py-8">Carregando histórico…</div>
            ) : (
              <>
                {/* Compras */}
                <div className="mb-8">
                  <h2 className="text-base sm:text-lg font-semibold text-white mb-4">💳 Compras</h2>
                  {!paymentHistory?.payments.length ? (
                    <p className="text-white/30 text-sm text-center py-6">Nenhuma compra realizada ainda.</p>
                  ) : (
                    <div className="space-y-2">
                      {paymentHistory.payments.map((p: any) => (
                        <div key={p.id} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          <div>
                            <p className="text-sm text-white font-medium">
                              {p.payment_method === 'pix' ? 'PIX' : p.payment_method === 'credit_card' ? 'Cartão' : p.payment_method === 'assinatura' ? 'Assinatura' : p.payment_method}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5">
                              {p.paid_at
                                ? new Date(p.paid_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : new Date(p.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-white">
                              R$ {Number(p.amount).toFixed(2).replace('.', ',')}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              p.payment_status === 'approved'
                                ? 'bg-green-500/20 text-green-300'
                                : p.payment_status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-300'
                                : 'bg-red-500/20 text-red-300'
                            }`}>
                              {p.payment_status === 'approved' ? 'Aprovado' : p.payment_status === 'pending' ? 'Pendente' : p.payment_status === 'cancelled' ? 'Cancelado' : p.payment_status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Transações de tokens */}
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-white mb-4">✨ Transações de Tokens</h2>
                  {!paymentHistory?.token_transactions.length ? (
                    <p className="text-white/30 text-sm text-center py-6">Nenhuma transação registrada.</p>
                  ) : (
                    <div className="space-y-2">
                      {paymentHistory.token_transactions.map((t: any) => (
                        <div key={t.id} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          <div>
                            <p className="text-sm text-white">{t.source_label}</p>
                            <p className="text-xs text-white/40 mt-0.5">
                              {new Date(t.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <span className={`text-sm font-bold ${t.type === 'credit' ? 'text-green-400' : t.type === 'debit' ? 'text-red-400' : 'text-yellow-400'}`}>
                            {t.type === 'credit' ? '+' : t.type === 'debit' ? '-' : '~'}{t.amount} token{t.amount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Aba: Segurança */}
        {activeTab === 'seguranca' && <div style={cardStyle} className="p-4 sm:p-8">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">Alterar Senha</h2>
          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <label className={labelClass}>Senha atual</label>
              <input
                type="password"
                name="current_password"
                value={passwordForm.current_password}
                onChange={handlePasswordChange}
                className={inputClass}
                required
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className={labelClass}>Nova senha</label>
              <input
                type="password"
                name="new_password"
                value={passwordForm.new_password}
                onChange={handlePasswordChange}
                className={inputClass}
                required
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className={labelClass}>Confirmar nova senha</label>
              <input
                type="password"
                name="confirm_password"
                value={passwordForm.confirm_password}
                onChange={handlePasswordChange}
                className={inputClass}
                required
                autoComplete="new-password"
              />
            </div>

            {passwordMsg && (
              <div
                className={`px-4 py-3 rounded-lg text-sm ${
                  passwordMsg.type === 'success'
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                    : 'bg-red-500/20 text-red-300 border border-red-500/30'
                }`}
              >
                {passwordMsg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={savingPassword}
              className="w-full py-3 rounded-xl font-semibold text-white transition-all"
              style={{
                background: savingPassword
                  ? 'rgba(139,92,246,0.4)'
                  : 'linear-gradient(to right, #7c3aed, #8b5cf6)',
              }}
            >
              {savingPassword ? 'Alterando...' : 'Alterar senha'}
            </button>
          </form>

          {/* Zona de perigo */}
          <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <p className="text-xs uppercase tracking-widest text-white/30 mb-3">Zona de perigo</p>
            {!deleteConfirm ? (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="w-full py-3 rounded-xl text-sm font-medium transition-all text-red-400/70 hover:text-red-400"
                style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}
              >
                Excluir minha conta
              </button>
            ) : (
              <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                <p className="text-sm text-red-300 leading-relaxed">
                  Esta ação é <strong>permanente e irreversível</strong>. Todos os seus dados, consultas, tokens e histórico serão apagados.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    disabled={deleting}
                    className="flex-1 py-2 rounded-lg text-sm text-white/50 hover:text-white/70 transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                    style={{ background: deleting ? 'rgba(239,68,68,0.4)' : 'rgba(239,68,68,0.7)' }}
                  >
                    {deleting ? 'Excluindo...' : 'Sim, excluir tudo'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>}
      </div>
    </div>
  );
};

export default ProfilePage;
